import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  applyNoc,
  getNocTypes,
  payNocSuccess,
  verifyStudent,
} from "../../store/actions/nocPublicAction";
import { NOC_DIGITAL_NOTE, openNocCheckout } from "../../utils/nocPay";
import { errorAlert, successAlert } from "../../utils/alert";

/*
  Public NOC apply.

  Step 1: identify the student with roll/enrollment + date of birth.
  Step 2: pick a NOC type and validity, pay, and the NOC is issued - the
  download link is shown straight away.
*/
const NocApply = () => {
  const dispatch = useDispatch();

  const [identifier, setIdentifier] = useState("");
  const [dob, setDob] = useState("");
  const [student, setStudent] = useState(null);

  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [validity, setValidity] = useState("3");

  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null); // { link }

  useEffect(() => {
    dispatch(getNocTypes()).then((rows) => setTypes(Array.isArray(rows) ? rows : []));
  }, [dispatch]);

  const selected = types.find((t) => String(t.id) === String(typeId));
  const fee = selected
    ? Number(validity) === 6
      ? Number(selected.fee_6m)
      : Number(selected.fee_3m)
    : 0;

  const verify = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !dob) {
      errorAlert("Please enter your roll/enrollment number and date of birth.");
      return;
    }
    setBusy(true);
    const body = new FormData();
    body.append("identifier", identifier.trim());
    body.append("dob", dob);
    const res = await dispatch(verifyStudent(body));
    setBusy(false);
    if (res.status === "success") {
      setStudent(res.data);
    } else {
      errorAlert(res.message || "No matching student found.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!typeId) return errorAlert("Please choose a NOC type.");

    setBusy(true);
    const body = new FormData();
    body.append("student_id", student.student_id);
    body.append("dob", dob);
    body.append("noc_type_id", typeId);
    body.append("validity_months", validity);
    const res = await dispatch(applyNoc(body));

    if (res.status !== "success") {
      errorAlert(res.message || "The NOC could not be applied for.");
      setBusy(false);
      return;
    }

    const d = res.data || {};
    if (d.free) {
      successAlert("NOC issued.");
      setIssued({ link: d.link });
      setBusy(false);
      return;
    }

    const pay = await openNocCheckout({ ...d, name: student.name });
    if (pay.payment_id) {
      const fd = new FormData();
      fd.append("noc_id", d.noc_id);
      fd.append("razorpay_payment_id", pay.payment_id);
      const done = await dispatch(payNocSuccess(fd));
      if (done.status === "success") {
        successAlert("Payment verified. NOC issued.");
        setIssued({ link: done.data?.link });
      } else {
        errorAlert(done.message || "Payment could not be verified.");
      }
    } else if (pay.error) {
      errorAlert(pay.error);
    }
    setBusy(false);
  };

  return (
    <div className="gallery-box">
      <div className="container">
        <div className="title-box">
          <div className="col-md-12 inner">
            <section className="innerbanner">
              <ol className="breadcrumb">
                <li>
                  <Link to="/" className="none">
                    Home
                  </Link>
                </li>
                <li className="active">Apply for NOC</li>
              </ol>
            </section>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card p-4 mt-3">
              <h4 className="mb-3">Apply for No Objection Certificate (NOC)</h4>

              {issued ? (
                <div className="text-center py-4">
                  <h5 className="text-success">Your NOC has been issued.</h5>
                  {issued.link ? (
                    <a
                      href={issued.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-comman mt-3"
                    >
                      Download NOC Certificate
                    </a>
                  ) : null}
                </div>
              ) : !student ? (
                <form onSubmit={verify}>
                  <p className="text-muted">
                    Enter your roll / enrollment number and date of birth to continue.
                  </p>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Roll / Enrollment No.</label>
                      <input
                        type="text"
                        className="form-control"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        className="form-control"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-comman" disabled={busy}>
                    {busy ? "Please wait..." : "Continue"}
                  </button>
                </form>
              ) : (
                <form onSubmit={submit}>
                  <div className="alert alert-light border mb-3">
                    <b>{student.name}</b>
                    <br />
                    Roll/Enroll: {student.roll_number}
                    {student.course ? ` | ${student.course}` : ""}
                    {student.center ? ` | ${student.center}` : ""}
                  </div>

                  {student.eligibility && student.eligibility.ok === false ? (
                    <>
                      <div className="alert alert-warning">
                        {student.eligibility.reason}
                      </div>
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => setStudent(null)}
                      >
                        Change student
                      </button>
                    </>
                  ) : (
                  <>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Purpose (NOC Type)</label>
                      <select
                        className="form-control"
                        value={typeId}
                        onChange={(e) => setTypeId(e.target.value)}
                      >
                        <option value="">-- Select NOC type --</option>
                        {types.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.subject} (Form {t.form_no})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Validity</label>
                      <div>
                        <label className="mr-3">
                          <input
                            type="radio"
                            checked={validity === "3"}
                            onChange={() => setValidity("3")}
                          />{" "}
                          03 Months{selected ? ` (₹${Number(selected.fee_3m)})` : ""}
                        </label>
                        <label>
                          <input
                            type="radio"
                            checked={validity === "6"}
                            onChange={() => setValidity("6")}
                          />{" "}
                          06 Months{selected ? ` (₹${Number(selected.fee_6m)})` : ""}
                        </label>
                      </div>
                    </div>
                  </div>

                  {selected && (
                    <p className="mb-2">
                      Amount payable : <b>₹{fee}</b>
                    </p>
                  )}
                  <p className="text-muted" style={{ fontSize: "13px" }}>
                    {NOC_DIGITAL_NOTE}
                  </p>

                  <button type="submit" className="btn btn-comman" disabled={busy || !typeId}>
                    {busy ? "Please wait..." : fee > 0 ? `Apply & Pay ₹${fee}` : "Apply for NOC"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light ml-2"
                    onClick={() => setStudent(null)}
                  >
                    Change student
                  </button>
                  </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NocApply;
